// docs/116 CI2/CI15/CI16: the inn's own furniture on the stage: the back canvas (gallery, stairs, the
// noticeboard, the kitchen door, the season's dressing) under everyone, and each piece with its own
// depth (armchairs, the lectern, stools, the dice table, the beds). Painted once per room and light.
import { memo, useEffect, useMemo, useRef } from 'react';
import type { InnPlan, Room } from '../../layout';
import type { Atlas } from '../../sprites';
import type { SeasonKey } from '../../../../../core/story/calendar';
import { innPieces, paintInnBack, paintPiece, type Piece } from '../paint';

interface Props { room: Room; plan: InnPlan; upstairs: boolean; night: boolean; season?: SeasonKey; feast: boolean; world?: Atlas; seed: number; scale: number }

function Canvas({ src, x, y, scale, z, cls }: { src: HTMLCanvasElement; x: number; y: number; scale: number; z: number; cls: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current?.getContext('2d');
    if (!c) return;
    c.imageSmoothingEnabled = false;
    c.clearRect(0, 0, c.canvas.width, c.canvas.height);
    c.drawImage(src, 0, 0);
  }, [src]);
  return <canvas ref={ref} className={`fk-inn__paint ${cls}`} width={src.width} height={src.height} aria-hidden="true"
    style={{ left: x * scale, top: y * scale, width: src.width * scale, height: src.height * scale, zIndex: z }} />;
}

export const Furniture = memo(function Furniture({ room, plan, upstairs, night, season, feast, world, seed, scale }: Props) {
  const back = useMemo(() => (world ? paintInnBack({ room, plan, night, ...(season ? { season } : {}), feast, world, seed, upstairs }) : undefined), [room, plan, night, season, feast, world, seed, upstairs]);
  const pieces = useMemo(() => (world ? innPieces(room, plan, upstairs, seed).map((p: Piece) => ({ p, cv: paintPiece(p, night) })) : []), [room, plan, upstairs, seed, night, world]);
  if (!back) return null;
  return (
    <>
      <Canvas src={back.base} x={0} y={0} scale={scale} z={0} cls="fk-inn__paint--back" />
      <Canvas src={back.glow} x={0} y={0} scale={scale} z={1} cls="fk-inn__paint--glow" />
      {pieces.map(({ p, cv }) => <Canvas key={p.key} src={cv} x={p.x} y={p.y} scale={scale} z={p.z} cls="fk-inn__paint--piece" />)}
    </>
  );
});
